import { Box, Typography } from '@mui/material';
import type { HostedReport } from '../types/report.types';
import { formatDateRange } from '../reportDate';
import { BODY_FG, HEADING_FG, MUTED_FG, RANK_OTHER_FG } from '../reportTokens';

/**
 * "{range} · {City, ST}" — the middle clause is dropped cleanly (no dangling
 * "·") when either half of the location is missing, which is always on a v2
 * snapshot and sometimes on v3 if `companyInfo.state` was never filled in.
 */
const formatRangeAndLocation = (
  range: string,
  city?: string,
  state?: string,
) => {
  const location = city && state ? `${city}, ${state}` : '';
  if (range && location) return `${range} · ${location}`;
  return range || location;
};

/**
 * The design's title block: eyebrow, business name, period, who this was
 * prepared by, then the week's opening sentence.
 */
function ReportIntro({ report }: { report: HostedReport }) {
  const range = formatDateRange(report.period.start, report.period.end);
  const rangeAndLocation = formatRangeAndLocation(
    range,
    report.company.city,
    report.company.state,
  );
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '13px', color: MUTED_FG, mb: '6px' }}>
        Weekly report
      </Typography>
      <Typography
        component='h1'
        sx={{
          // 34px, not the app's 28px hero. This page is a document a client
          // opens on its own, and the design sizes its title accordingly.
          fontSize: '34px',
          fontWeight: 800,
          letterSpacing: '-0.02em',
          lineHeight: 1.15,
          color: HEADING_FG,
          m: 0,
        }}
      >
        {report.company.name}
      </Typography>
      {rangeAndLocation && (
        <Typography sx={{ fontSize: '15px', color: BODY_FG, mt: '6px' }}>
          {rangeAndLocation}
        </Typography>
      )}
      {report.branding?.brandName && (
        <Typography sx={{ fontSize: '13px', color: MUTED_FG, mt: '8px' }}>
          Presented by{' '}
          <Box component='span' sx={{ fontWeight: 600, color: RANK_OTHER_FG }}>
            {report.branding.brandName}
          </Box>
        </Typography>
      )}
      {report.intro && (
        <Typography
          sx={{
            fontSize: '16px',
            color: BODY_FG,
            lineHeight: 1.7,
            mt: '16px',
            textWrap: 'pretty',
          }}
        >
          {report.intro}
        </Typography>
      )}
    </Box>
  );
}

export default ReportIntro;
